// src/pages/MyBookings.jsx
import { useEffect, useState, useMemo } from "react";
import apiClient from "../api"; // ✅ shared API client
import { Link, useNavigate } from "react-router-dom";
import {
  FaBus,
  FaArrowRight,
  FaExclamationCircle,
  FaDownload,
} from "react-icons/fa";

/* ---------- Matte palette (aligned with ConfirmBooking / SearchResults) ---------- */
const PALETTE = {
  primary: "#C74A50",
  bg: "#F5F6F8",
  surface: "#FFFFFF",
  surfaceAlt: "#FAFBFC",
  border: "#E5E7EB",
  text: "#111827",
  textSubtle: "#6B7280",
  pillBg: "#F3F4F6",
  successBg: "#DCFCE7",
  successText: "#166534",
  warningBg: "#FEF3C7",
  warningText: "#92400E",
};

/* ---------- auth helpers (same pattern used elsewhere) ---------- */
const getAuthToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("authToken") ||
  localStorage.getItem("jwt") ||
  sessionStorage.getItem("token") ||
  null;

const buildAuthConfig = (token) =>
  token ? { headers: { Authorization: `Bearer ${token}` } } : {};

/* ---------- small shared UI bits ---------- */
const StatusPill = ({ status }) => {
  const paid = status === "Paid";
  const bg = paid ? PALETTE.successBg : PALETTE.warningBg;
  const color = paid ? PALETTE.successText : PALETTE.warningText;
  return (
    <span
      className="text-[11px] font-semibold px-3 py-1 rounded-full whitespace-nowrap"
      style={{ background: bg, color }}
    >
      {status}
    </span>
  );
};

const Chip = ({ label }) => (
  <span
    className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium mr-1 mt-1"
    style={{ background: PALETTE.pillBg, color: PALETTE.textSubtle }}
  >
    {label}
  </span>
);

/* ---- Label + pill styles copied from ConfirmBooking ---- */
const Label = ({ children }) => (
  <span
    className="block text-[11px] font-semibold mb-1 uppercase tracking-wide"
    style={{ color: PALETTE.textSubtle }}
  >
    {children}
  </span>
);

const Pill = ({ children }) => (
  <span
    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
    style={{ background: PALETTE.pillBg, color: PALETTE.text }}
  >
    {children}
  </span>
);

const SoftPill = ({ children, bg }) => (
  <span
    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
    style={{ background: bg, color: PALETTE.text }}
  >
    {children}
  </span>
);

const DatePill = ({ children }) => (
  <SoftPill bg="#FFF7ED">{children}</SoftPill>
);

const SeatPill = ({ children }) => <Pill>{children}</Pill>;

/* ---------- skeleton (restyled to match matte cards) ---------- */
const BookingCardSkeleton = () => (
  <div
    className="rounded-2xl p-5 animate-pulse"
    style={{ background: PALETTE.surface, border: `1px solid ${PALETTE.border}` }}
  >
    <div className="flex justify-between items-start border-b border-gray-100 pb-3">
      <div className="space-y-2">
        <div className="h-5 w-40 bg-gray-200 rounded" />
        <div className="h-4 w-32 bg-gray-200 rounded" />
      </div>
      <div className="h-5 w-20 bg-gray-200 rounded-full" />
    </div>
    <div className="mt-4 space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-5 h-5 bg-gray-200 rounded" />
        <div className="h-4 w-1/2 bg-gray-200 rounded" />
      </div>
      <div className="flex items-center gap-3">
        <div className="w-5 h-5 bg-gray-200 rounded" />
        <div className="h-4 w-2/3 bg-gray-200 rounded" />
      </div>
    </div>
    <div className="flex justify-between items-end mt-4 pt-4 border-t border-gray-100">
      <div className="h-8 w-24 bg-gray-200 rounded-lg" />
      <div className="h-9 w-32 bg-gray-200 rounded-lg" />
    </div>
  </div>
);

const MyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBookings = async () => {
      const token = getAuthToken();
      if (!token) {
        console.error("No token found. Please login.");
        setLoading(false);
        return;
      }
      try {
        // ✅ IMPORTANT: relative path to your API base; no localhost anywhere
        const res = await apiClient.get("/bookings/me", buildAuthConfig(token));
        const safeBookings = Array.isArray(res.data) ? res.data : [];
        setBookings(safeBookings);
      } catch (err) {
        console.error("Failed to load bookings", err?.response?.data || err);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, []);

  // ✅ Only show paid bookings
  const paidBookings = useMemo(
    () => bookings.filter((b) => b?.paymentStatus === "Paid"),
    [bookings]
  );

  const sortedBookings = useMemo(
    () =>
      [...paidBookings].sort(
        (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0)
      ),
    [paidBookings]
  );

  const handleDownloadTicket = (booking) => {
    const matchedBoardingPoint =
      booking?.bus?.boardingPoints?.find((p) => p?.point === booking?.from) ||
      null;

    const matchedDroppingPoint =
      booking?.bus?.droppingPoints?.find((p) => p?.point === booking?.to) ||
      null;

    // ✅ Build only the fields DownloadTicket really needs
    const bookingDetails = {
      bus: {
        name: booking?.bus?.name || "",
        from: booking?.bus?.from || "",
        to: booking?.bus?.to || "",
        operatorName: booking?.bus?.operatorName || "",
        operator: booking?.bus?.operator || "",
      },
      passenger: {
        name: booking?.passengerInfo?.fullName || "",
        email: booking?.passengerInfo?.email || "",
        mobile: booking?.passengerInfo?.phone || "",
        nic: booking?.passengerInfo?.nic || "",
      },
      passengers: Array.isArray(booking?.passengers) ? booking.passengers : [],
      date: booking?.date || "",
      selectedSeats: Array.isArray(booking?.selectedSeats)
        ? booking.selectedSeats
        : [],
      priceDetails: {
        totalPrice: Number(booking?.totalAmount || 0),
        convenienceFee: Number(booking?.convenienceFee || 0),
        basePrice:
          Number(booking?.totalAmount || 0) - Number(booking?.convenienceFee || 0),
      },
      boardingPoint: matchedBoardingPoint
        ? {
            point: matchedBoardingPoint.point || "",
            time: matchedBoardingPoint.time || booking?.departureTime || "",
            city: matchedBoardingPoint.city || "",
          }
        : {
            point: booking?.from || "",
            time: booking?.departureTime || "",
            city: "",
          },
      droppingPoint: matchedDroppingPoint
        ? {
            point: matchedDroppingPoint.point || "",
            time: matchedDroppingPoint.time || "",
            city: matchedDroppingPoint.city || "",
          }
        : {
            point: booking?.to || "",
            time: "",
            city: "",
          },
      departureTime: booking?.departureTime || "",
      bookingId: booking?._id || "",
      bookingNo: booking?.bookingNo || "",
      bookingNoShort: booking?.bookingNoShort || "",
    };

    try {
      sessionStorage.setItem(
        "rb_ticket_payload",
        JSON.stringify({ bookingDetails })
      );
    } catch (err) {
      console.warn("Failed to save ticket payload to sessionStorage", err);
    }

    navigate("/download-ticket", { state: { bookingDetails } });
  };

  const getReadableDate = (dateString) => {
    if (!dateString) return "N/A";
    const dateObj = new Date(dateString);
    if (Number.isNaN(dateObj.getTime())) return "N/A";
    return dateObj.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div
      className="min-h-screen text-[15px] md:text-base"
      style={{ background: PALETTE.bg }}
    >
      {/* Matte top bar - same style as ConfirmBooking */}
      <div
        className="sticky top-0 z-30"
        style={{
          background: PALETTE.primary,
          paddingTop: "env(safe-area-inset-top)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col gap-1">
          <p className="text-white text-base font-semibold leading-tight">
            My Bookings
          </p>
          <p className="text-white/90 text-xs">
            View your upcoming and past trips, and download tickets anytime.
          </p>
        </div>
      </div>

      {/* Page body */}
      <div className="max-w-6xl mx-auto px-4 py-4 sm:py-6">
        {loading ? (
          <div className="space-y-4">
            <BookingCardSkeleton />
            <BookingCardSkeleton />
          </div>
        ) : sortedBookings.length === 0 ? (
          <div
            className="text-center rounded-2xl px-6 py-10 sm:px-10"
            style={{
              background: PALETTE.surface,
              border: `1px solid ${PALETTE.border}`,
            }}
          >
            <FaExclamationCircle className="mx-auto text-5xl text-gray-300 mb-4" />
            <h3
              className="text-xl font-bold"
              style={{ color: PALETTE.text }}
            >
              No bookings found
            </h3>
            <p
              className="mt-2 mb-6 text-sm sm:text-base"
              style={{ color: PALETTE.textSubtle }}
            >
              You haven&apos;t booked any trips yet. Let&apos;s find one for you!
            </p>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 rounded-full text-sm sm:text-base font-semibold transition-transform hover:scale-[1.02]"
              style={{
                background: "var(--rb-primary, #D84E55)",
                color: "#FFFFFF",
              }}
            >
              Book a trip now
            </Link>
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-5">
            {sortedBookings.map((booking) => (
              <div
                key={booking._id}
                className="rounded-2xl p-4 sm:p-5"
                style={{
                  background: PALETTE.surface,
                  border: `1px solid ${PALETTE.border}`,
                }}
              >
                {/* Header: bus + route + status */}
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 border-b border-gray-100 pb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <FaBus className="text-sm text-red-500 flex-shrink-0" />
                      <h3
                        className="text-base sm:text-lg font-semibold truncate"
                        style={{ color: PALETTE.text }}
                      >
                        {booking?.bus?.name || "Bus"}
                      </h3>
                    </div>
                    <p
                      className="mt-1 text-xs sm:text-sm flex items-center gap-2 text-gray-500"
                      style={{ color: PALETTE.textSubtle }}
                    >
                      {booking?.bus?.from || booking?.from || "—"}{" "}
                      <FaArrowRight className="text-[10px]" />{" "}
                      {booking?.bus?.to || booking?.to || "—"}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <StatusPill status={booking?.paymentStatus || "Pending"} />
                    {booking?.bookingNo ? (
                      <p className="text-[11px] text-gray-500">
                        Booking No:{" "}
                        <span className="font-semibold tabular-nums text-gray-700">
                          {booking.bookingNo}
                        </span>
                      </p>
                    ) : null}
                  </div>
                </div>

                {/* Journey meta pills (date / type / seat count) */}
                <div className="mt-3 flex flex-wrap gap-2">
                  <DatePill>
                    {getReadableDate(booking?.date)}{" "}
                    <span className="text-[11px] mx-1">at</span>
                    <span className="tabular-nums">
                      {booking?.departureTime || "—"}
                    </span>
                  </DatePill>
                  {booking?.bus?.busType && (
                    <SoftPill bg={PALETTE.surfaceAlt}>
                      {booking.bus.busType}
                    </SoftPill>
                  )}
                  <SeatPill>
                    {Array.isArray(booking?.selectedSeats)
                      ? booking.selectedSeats.length
                      : 0}{" "}
                    Seat
                    {(Array.isArray(booking?.selectedSeats)
                      ? booking.selectedSeats.length
                      : 0) > 1
                      ? "s"
                      : ""}
                  </SeatPill>
                </div>

                {/* Details: labels + plain text (no icons) */}
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-[13px] sm:text-sm">
                  <div>
                    <Label>Travel date</Label>
                    <p className="font-medium" style={{ color: PALETTE.text }}>
                      {getReadableDate(booking?.date)}
                    </p>
                  </div>

                  <div>
                    <Label>Departure time</Label>
                    <p
                      className="font-medium tabular-nums"
                      style={{ color: PALETTE.text }}
                    >
                      {booking?.departureTime || "—"}
                    </p>
                  </div>

                  <div>
                    <Label>Boarding</Label>
                    <p
                      className="font-medium truncate"
                      style={{ color: PALETTE.text }}
                    >
                      {booking?.from || "—"}
                    </p>
                  </div>
                </div>

                {/* Seats list as pills */}
                <div className="mt-3">
                  <Label>Seats</Label>
                  <div className="flex flex-wrap gap-2">
                    {(Array.isArray(booking?.selectedSeats)
                      ? booking.selectedSeats
                      : []
                    ).map((s) => (
                      <SeatPill key={s}>Seat {s}</SeatPill>
                    ))}
                  </div>
                </div>

                {/* Extra chips */}
                <div className="mt-3 flex flex-wrap gap-1">
                  {booking?.bus?.busType && <Chip label={booking.bus.busType} />}
                  {booking?.bus?.features?.wifi && <Chip label="Wi-Fi" />}
                  {booking?.bus?.features?.chargingPort && (
                    <Chip label="Charging Port" />
                  )}
                  {booking?.createdAt && (
                    <Chip
                      label={`Booked on ${new Date(
                        booking.createdAt
                      ).toLocaleDateString("en-GB")}`}
                    />
                  )}
                </div>

                {/* Footer: price + action */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
                  <div>
                    <p
                      className="text-xs uppercase tracking-wide font-medium"
                      style={{ color: PALETTE.textSubtle }}
                    >
                      Total paid
                    </p>
                    <p
                      className="text-lg sm:text-xl font-bold tabular-nums"
                      style={{ color: "#16A34A" }}
                    >
                      Rs. {Number(booking?.totalAmount || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <button
                      onClick={() => handleDownloadTicket(booking)}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-transform hover:scale-[1.02]"
                      style={{
                        background: "var(--rb-primary, #D84E55)",
                        color: "#FFFFFF",
                      }}
                    >
                      <FaDownload className="text-xs" />
                      <span>Download ticket</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBookings;
